import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../AuthProvider';
import { useSubscription } from '../../../context/SubscriptionContext';
import {
  useDataLoader, useSaveManagement, useSprintActions, useTemplateActions,
  useCampaignNesting, useUserSettings, useAppNavigation, useAppModals,
  useMultiCampaignData, useTemplateFromCampaign, useTabIntegration,
  useOrphanCampaignRecovery,
} from '../../../hooks';
import { detectOrphanedCampaigns } from '../../Sidebar/utils/orphanDetection';
import { installNestedLinkDiagnostics } from '../../../utils/nestedLinkAudit';
import {
  queueCampaignMissionFocus,
  setNavigateToCampaignHandler,
  setOpenCampaignInNewTabHandler,
} from '../../../utils/navigationBridge';
import { useAppUrlNavigation } from './useAppUrlNavigation';
import { useViewActions } from './useViewActions';
import { useNestingNotification } from './useNestingNotification';
import { useMissionToSidebarBridge } from './useMissionToSidebarBridge';
import { useWorkspaceReset } from './useWorkspaceReset';
import { VIEWS } from '../constants';
import { useTabs } from '../../../context/TabsContext';
import { isDeadContentTab, shouldRewriteToCampaignsHome } from './isDeadContentTab';

/**
 * Orchestrates all App-level data, navigation, and action hooks.
 * Returns everything needed to render AppShell.
 */
export function useAppContent() {
  const {
    user,
    logout,
    loading: authLoading,
    workspaceId,
    workspaces,
    setWorkspace,
  } = useAuth();
  const { loading: subscriptionLoading } = useSubscription();
  const { showInActiveTab, tabs, activeTab, closeTab, isLoaded: tabsLoaded } = useTabs();
  const {
    sprints, setSprints, templates,
    loading: dataLoading, itemLoadErrors, loadFullSprint, loadFullTemplate, getFirstActiveSprint,
  } = useDataLoader();

  const { saveStatus, saveSprint, saveSprintImmediate, flushAllPendingSaves, cancelPendingSaveForSprint } = useSaveManagement();
  const {
    loading: settingsLoading,
    sprintOrder, setSprintOrder, lastSelectedCampaignId, setLastSelectedCampaignId,
    flowViewVisibility, setFlowViewVisibility,
    hideViewExplainerModal, setHideViewExplainerModal,
    preferences, updatePreferences,
  } = useUserSettings();

  const {
    currentView, setCurrentView, selectedSprintId, setSelectedSprintId,
    selectedTemplateId, setSelectedTemplateId, preferredDetailView, pendingMissionFocus,
    activeView, isLoadingItem, setIsLoadingItem, handleSelectSprint, handleSelectView, updateActiveView,
    clearActiveView, handleSelectTemplate, handleSidebarNavigate, clearPreferredDetailView, clearPendingMissionFocus,
    setPreferredDetailView, setPendingMissionFocus,
  } = useAppNavigation({ loadFullSprint, loadFullTemplate, setLastSelectedCampaignId, VIEWS });

  const {
    handleAddSprint, handleUpdateSprint, handleRenameSprint, handleDeleteSprint,
    handleRestoreSprint, handlePermanentlyDeleteSprint, handleEmptyTrash,
    handleSprintCreated, handleSprintIconChange,
  } = useSprintActions({
    sprints, setSprints, selectedSprintId, setSelectedSprintId,
    selectedTemplateId, setSelectedTemplateId, setCurrentView, saveSprint, saveSprintImmediate, VIEWS,
  });

  const {
    handleUpdateTemplate, handleRenameTemplate,
    handleDuplicateTemplate, handleTemplateCreated, handleTemplateIconChange,
  } = useTemplateActions({
    sprints,
    setSprints,
    loadFullSprint,
    handleSelectTemplate,
    handleUpdateSprint,
    handleRenameSprint,
    handleSprintIconChange,
  });

  const { handleCreateTemplateFromCampaign } = useTemplateFromCampaign({
    sprints, loadFullSprint, setSprints, handleSelectTemplate,
  });

  const { handleNestCampaign, handleReorderNestedCampaign, fixOrphanedCampaigns, isNesting } = useCampaignNesting(setSprints);
  const { nestingComplete, handleNestCampaignWithNotification } = useNestingNotification(isNesting, handleNestCampaign);

  useMissionToSidebarBridge(setSprints, cancelPendingSaveForSprint, sprints);

  const {
    isCreateSprintOpen, preselectedTemplate, parentSprintIdForNesting,
    openCreateSprintModal, openCreateNestedSprintModal, closeCreateSprintModal, onSprintCreated,
    isCreateTemplateOpen, openCreateTemplateModal, closeCreateTemplateModal, onTemplateCreated, handleUseTemplate,
    isSettingsOpen, openSettingsModal, closeSettingsModal,
    setSelectSprintHandler, setSelectTemplateHandler,
  } = useAppModals({
    handleSprintCreated, handleTemplateCreated, handleSelectSprint, handleSelectTemplate,
    handleNestCampaign: handleNestCampaignWithNotification,
    handleAddSprint,
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const { multiCampaignData, isLoadingViewData } = useMultiCampaignData({
    activeView, sprints, loadFullSprint,
  });

  const { isNestedDataLoading } = useMemo(
    () => detectOrphanedCampaigns(sprints),
    [sprints]
  );

  const {
    removingCampaignId,
    handleRenameView,
    handleViewIconChange,
    handleRemoveCampaignFromView,
    handleAddCampaignToView,
  } = useViewActions({ activeView, updateActiveView, multiCampaignData });

  const selectedSprint = sprints.find(s => s.id === selectedSprintId && !s.isTemplate);
  const selectedTemplate = sprints.find(s => s.id === selectedTemplateId && (s.isTemplate || s.type === 'template'));
  // Settings are per-workspace (lastSelectedCampaignId drives startup selection),
  // so startup must also wait for them after a workspace switch.
  const isUpstreamLoading = authLoading || subscriptionLoading || dataLoading || settingsLoading;

  // Owned here (not in useAppUrlNavigation) so useTabIntegration gets the real value
  const [isInitialStartupComplete, setIsInitialStartupComplete] = useState(false);

  const {
    handleTabClick,
    handleSelectSprintAndUpdateTab,
    handleSelectTemplateAndUpdateTab,
    handleSelectViewAndUpdateTab,
    handleOpenInNewTab,
    handleNewTab,
    handleViewModeChange: handleTabViewModeChange,
    syncTabToContent,
  } = useTabIntegration({
    sprints,
    selectedSprint,
    selectedTemplate,
    selectedSprintId,
    selectedTemplateId,
    setSelectedSprintId,
    setSelectedTemplateId,
    setCurrentView,
    setLastSelectedCampaignId,
    loadFullSprint,
    loadFullTemplate,
    handleSelectSprint,
    handleSelectTemplate,
    handleSelectView,
    setIsLoadingItem,
    setPreferredDetailView,
    setPendingMissionFocus,
    clearActiveView,
    activeView,
    currentView,
    preferences,
    isInitialStartupComplete,
    VIEWS,
  });

  const openDocsArea = useCallback(() => {
    setCurrentView(VIEWS.DOCS);
  }, [setCurrentView]);

  const openCampaignsHomeArea = useCallback(() => {
    clearActiveView?.();
    setSelectedSprintId(null);
    setSelectedTemplateId(null);
    showInActiveTab({
      type: 'campaigns',
      targetId: null,
      label: 'Campaigns',
      icon: null,
      viewMode: null,
    });
    setCurrentView(VIEWS.EMPTY);
  }, [clearActiveView, setSelectedSprintId, setSelectedTemplateId, showInActiveTab, setCurrentView]);

  const openDashboardsArea = useCallback(() => {
    setCurrentView(VIEWS.DASHBOARDS);
  }, [setCurrentView]);

  const {
    handleSelectSprintWithUrl,
    handleSelectTemplateWithUrl,
    handleSelectViewWithUrl,
    handleOpenDocsWithUrl,
    handleOpenDashboardsWithUrl,
    handleTabClickWithUrl,
    handleNewTabWithUrl,
    handleViewModeChange,
    handleCustomViewChange,
    handleHistoryNavigate,
    navigateToCampaigns,
    pushCampaignUrl,
    clearMissionId,
    resetStartup,
  } = useAppUrlNavigation({
    sprints,
    preferences,
    lastSelectedCampaignId,
    handleSelectSprint,
    handleSelectTemplate,
    handleSelectView,
    handleSidebarNavigate,
    openDocsArea,
    openDashboardsArea,
    openCampaignsHomeArea,
    openSettingsModal,
    setPreferredDetailView,
    setPendingMissionFocus,
    getFirstActiveSprint,
    handleSelectSprintAndUpdateTab,
    handleSelectTemplateAndUpdateTab,
    handleSelectViewAndUpdateTab,
    handleTabClick,
    handleNewTab,
    handleTabViewModeChange,
    syncTabToContent,
    isUpstreamLoading,
    isInitialStartupComplete,
    setIsInitialStartupComplete,
  });

  // A content chip that no longer has a place (trashed campaign) must not
  // own the main view. Do not treat "sprint not selected yet" as dead —
  // restore applies the campaign after paint, and rewriting here turned
  // the restored chip into Campaigns Atlas on every reopen / login.
  useEffect(() => {
    if (!isInitialStartupComplete || !tabsLoaded) return;

    if (shouldRewriteToCampaignsHome({ tabsLength: tabs.length, activeTab, sprints })) {
      openCampaignsHomeArea();
      navigateToCampaigns({ replace: true });
    }

    tabs.forEach((tab) => {
      if (activeTab && tab.id === activeTab.id) return;
      if (isDeadContentTab(tab, sprints)) closeTab(tab.id);
    });
  }, [
    isInitialStartupComplete,
    tabsLoaded,
    tabs,
    activeTab,
    sprints,
    openCampaignsHomeArea,
    navigateToCampaigns,
    closeTab,
  ]);

  const openWorkspaceAreaWithUrl = useCallback(() => {
    openCampaignsHomeArea();
    navigateToCampaigns();
  }, [openCampaignsHomeArea, navigateToCampaigns]);

  useWorkspaceReset({
    workspaceId,
    flushAllPendingSaves,
    setSprints,
    clearActiveView,
    setSelectedSprintId,
    setSelectedTemplateId,
    setCurrentView,
    setPreferredDetailView,
    setPendingMissionFocus,
    setIsLoadingItem,
    resetStartup,
  });

  useEffect(() => {
    setSelectSprintHandler(handleSelectSprintWithUrl);
    // Also expose navigation to non-view code (e.g. clickable toasts)
    setNavigateToCampaignHandler(handleSelectSprintWithUrl);
    return () => setNavigateToCampaignHandler(null);
  }, [setSelectSprintHandler, handleSelectSprintWithUrl]);

  useEffect(() => {
    const view = preferences.defaultView || 'flow';
    setOpenCampaignInNewTabHandler((sprint, options = {}) => {
      if (!sprint?.id) return;
      const known = sprints.find((entry) => entry.id === sprint.id);
      const item = known
        ? { id: known.id, name: known.name, icon: known.icon }
        : { id: sprint.id, name: sprint.name || 'Campaign', icon: sprint.icon || null };
      if (options.missionId) queueCampaignMissionFocus(sprint.id, options.missionId);
      handleOpenInNewTab(item, 'campaign', {
        view,
        missionId: options.missionId || null,
      });
      pushCampaignUrl(sprint.id, {
        viewMode: view,
        ...(options.missionId ? { missionId: options.missionId } : {}),
      });
    });
    return () => setOpenCampaignInNewTabHandler(null);
  }, [sprints, handleOpenInNewTab, pushCampaignUrl, preferences.defaultView]);

  useEffect(() => {
    setSelectTemplateHandler(handleSelectTemplateWithUrl);
  }, [setSelectTemplateHandler, handleSelectTemplateWithUrl]);

  const handleMissionFocused = useCallback(() => {
    clearPendingMissionFocus();
    clearMissionId();
  }, [clearPendingMissionFocus, clearMissionId]);

  const showLoadingOverlay = !isInitialStartupComplete;

  useOrphanCampaignRecovery({
    sprints,
    dataLoading,
    isLoadingViewData,
    isInitialStartupComplete,
    handleDeleteSprint,
  });

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    installNestedLinkDiagnostics({ sprints, loadFullSprint });
  }, [sprints, loadFullSprint]);

  return {
    sidebarCollapsed,
    setSidebarCollapsed,
    showLoadingOverlay,
    sprints,
    templates,
    saveStatus,
    isNesting,
    nestingComplete,
    dataLoading,
    isLoadingViewData,
    isNestedDataLoading,
    currentView,
    selectedSprintId,
    selectedTemplateId,
    activeView,
    sprintOrder,
    user,
    workspaceId,
    workspaces,
    isInitialStartupComplete,
    isLoadingItem,
    itemLoadErrors,
    multiCampaignData,
    selectedSprint,
    selectedTemplate,
    preferences,
    flowViewVisibility,
    hideViewExplainerModal,
    preferredDetailView,
    pendingMissionFocus,
    removingCampaignId,
    handleTabClickWithUrl,
    handleNewTab: handleNewTabWithUrl,
    handleHistoryNavigate,
    handleSidebarNavigate,
    handleSelectSprintWithUrl,
    handleSelectTemplateWithUrl,
    openCreateSprintModal,
    openCreateNestedSprintModal,
    openCreateTemplateModal,
    handleRenameSprint,
    handleSprintIconChange,
    handleDeleteSprint,
    handleRestoreSprint,
    handlePermanentlyDeleteSprint,
    handleEmptyTrash,
    handleRenameTemplate,
    handleDuplicateTemplate,
    handleUseTemplate,
    handleTemplateIconChange,
    handleCreateTemplateFromCampaign,
    handleNestCampaignWithNotification,
    handleReorderNestedCampaign,
    openSettingsModal,
    handleOpenDocsWithUrl,
    openWorkspaceArea: openWorkspaceAreaWithUrl,
    openDashboardsArea: handleOpenDashboardsWithUrl,
    setSprintOrder,
    handleSelectViewWithUrl,
    handleOpenInNewTab,
    logout,
    setWorkspace,
    setFlowViewVisibility,
    setHideViewExplainerModal,
    handleAddSprint,
    handleUpdateSprint,
    handleRemoveCampaignFromView,
    handleAddCampaignToView,
    handleRenameView,
    handleViewIconChange,
    handleUpdateTemplate,
    clearPreferredDetailView,
    handleMissionFocused,
    handleViewModeChange,
    handleCustomViewChange,
    isCreateSprintOpen,
    closeCreateSprintModal,
    onSprintCreated,
    preselectedTemplate,
    parentSprintIdForNesting,
    loadFullSprint,
    isCreateTemplateOpen,
    closeCreateTemplateModal,
    onTemplateCreated,
    isSettingsOpen,
    closeSettingsModal,
    updatePreferences,
  };
}
